import React from 'react';
import { MapPin, Search, Filter, X, BarChart3 } from 'lucide-react';
import AdvancedStats from './AdvancedStats';
import ThemeToggle from './ThemeToggle';
import { icons, TERRITOIRE_PRESETS } from './mapHelpers';

export default function MapOverlays(ctx) {
  const {
    activeTerritoire, autoRefresh, fetchMapData, filteredData, filters, focusTerritoire,
    geoCounts, getAuthHeader, handleAdvancedSearch, handleSearch, heatmapEnabled, heatmapPoints,
    isAdminView, isConnected, lastUpdate, loading, mapCenter, mapData,
    mapZoom, refreshInterval, searchFilters, searchResults, searchTerm, setActiveTerritoire,
    setAutoRefresh, setFilters, setHeatmapEnabled, setLoading, setMapCenter, setMapData,
    setMapZoom, setRefreshInterval, setSearchFilters, setSearchTerm, setShowAdvancedSearch, setShowAdvancedStats,
    setShowFilters, setTheme, setTimelineFilter, showAdvancedSearch, showAdvancedStats, showFilters,
    stats, theme, timelineFilter, toggleFilter, visibleMarkerCount,
  } = ctx;

  return (
    <>
      {/* Statistiques temps réel */}
      {isAdminView && (
        <div className="map-stats">
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#fef3c7' }}>🏗️</div>
            <div className="stat-content">
              <h4>{stats.chantiers}</h4>
              <p>Chantiers</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#dbeafe' }}>👥</div>
            <div className="stat-content">
              <h4>{stats.membres}</h4>
              <p>Membres</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#fee2e2' }}>⚠️</div>
            <div className="stat-content">
              <h4>{stats.dommages}</h4>
              <p>Dommages</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#ede9fe' }}>🔧</div>
            <div className="stat-content">
              <h4>{stats.travaux}</h4>
              <p>Travaux</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#d1fae5' }}>🤝</div>
            <div className="stat-content">
              <h4>{stats.coopers}</h4>
              <p>COOPER's</p>
            </div>
          </div>
        </div>
      )}
      
      {/* V37.41 — Barre de recherche LIVE (filtre les markers a la frappe) */}
      <div className="map-search" data-testid="map-search-bar">
        <Search className="h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Rechercher par nom, email, ville, territoire..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="map-search-input"
        />
        {searchTerm ? (
          <button
            onClick={() => setSearchTerm('')}
            title="Effacer la recherche"
            style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#fca5a5' }}
            data-testid="map-search-clear"
          >
            <X className="h-4 w-4" />
          </button>
        ) : (
          <button onClick={handleSearch} title="Recherche avancée">
            Avancée
          </button>
        )}
      </div>

      {/* V37.41 — Bandeau vide : aucun marker affiche (pourquoi + CTA) */}
      {!loading && visibleMarkerCount === 0 && (
        <div
          data-testid="map-empty-banner"
          style={{
            position: 'absolute', top: 88, left: '50%', transform: 'translateX(-50%)',
            zIndex: 1000, maxWidth: 560, padding: '14px 20px',
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.97), rgba(30, 41, 59, 0.95))',
            border: '1px solid rgba(59, 130, 246, 0.45)', borderRadius: 12,
            color: '#e5e7eb', boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <MapPin className="h-5 w-5 text-blue-300 shrink-0" style={{ marginTop: 2 }} />
            <div style={{ flex: 1, fontSize: 13, lineHeight: 1.5 }}>
              <div style={{ fontWeight: 600, color: '#93c5fd', marginBottom: 4 }}>
                Aucun marqueur affiché sur la carte
              </div>
              {searchTerm ? (
                <span>
                  Aucun résultat ne correspond à <strong>« {searchTerm} »</strong>{' '}
                  parmi les {stats.membres} membres, {stats.coopers} COOPER&apos;s et {stats.chantiers} chantiers.
                </span>
              ) : (
                <span>
                  Géolocalisation manquante : <strong>{geoCounts.membres.geo}/{geoCounts.membres.total}</strong> membres,{' '}
                  <strong>{geoCounts.coopers.geo}/{geoCounts.coopers.total}</strong> COOPER&apos;s et{' '}
                  <strong>{geoCounts.chantiers.geo}/{geoCounts.chantiers.total}</strong> chantiers ont des coordonnées GPS.
                  {' '}Lancez la synchronisation GPS depuis la barre supérieure pour les afficher.
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* V37.41 — Sidebar de resultats de recherche (visible des qu'on tape) */}
      {searchTerm && searchResults.length > 0 && (
        <div
          data-testid="map-search-results"
          style={{
            position: 'absolute', top: 88, right: 16, zIndex: 1000,
            width: 340, maxHeight: 'calc(100vh - 160px)', overflowY: 'auto',
            background: 'rgba(15, 23, 42, 0.96)',
            border: '1px solid rgba(59, 130, 246, 0.4)', borderRadius: 12,
            boxShadow: '0 12px 32px rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)',
          }}
        >
          <div style={{
            padding: '12px 16px', borderBottom: '1px solid rgba(59, 130, 246, 0.25)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{ color: '#93c5fd', fontWeight: 600, fontSize: 13 }}>
              {searchResults.length} résultat{searchResults.length > 1 ? 's' : ''}
            </span>
            <button
              onClick={() => setSearchTerm('')}
              style={{ background: 'none', border: 'none', color: '#cbd5e1', cursor: 'pointer' }}
              data-testid="map-search-results-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {searchResults.map((r) => {
            const typeIcons = { membre: '👥', cooper: '🤝', chantier: '🏗️', dommage: '⚠️', travaux: '🔧' };
            const typeColors = {
              membre: '#3b82f6', cooper: '#10b981', chantier: '#f59e0b',
              dommage: '#ef4444', travaux: '#8b5cf6',
            };
            const onClick = () => {
              if (r.hasGeo) {
                setMapCenter([r.raw.geo_location.latitude, r.raw.geo_location.longitude]);
                setMapZoom(14);
              }
            };
            return (
              <button
                key={r.id}
                onClick={onClick}
                disabled={!r.hasGeo}
                data-testid={`map-result-${r.id}`}
                style={{
                  width: '100%', display: 'flex', gap: 10, alignItems: 'center',
                  padding: '10px 16px', background: 'transparent', border: 'none',
                  borderBottom: '1px solid rgba(148, 163, 184, 0.08)',
                  textAlign: 'left', cursor: r.hasGeo ? 'pointer' : 'not-allowed',
                  color: '#e5e7eb', opacity: r.hasGeo ? 1 : 0.55,
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => r.hasGeo && (e.currentTarget.style.background = 'rgba(59, 130, 246, 0.12)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <span style={{ fontSize: 20 }}>{typeIcons[r.type]}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: typeColors[r.type], textTransform: 'capitalize' }}>
                    {r.label}
                  </span>
                  <span style={{ display: 'block', fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.sub}
                  </span>
                </span>
                {!r.hasGeo && (
                  <span style={{
                    fontSize: 9, padding: '2px 6px', borderRadius: 4,
                    background: 'rgba(245, 158, 11, 0.2)', color: '#fcd34d', whiteSpace: 'nowrap',
                  }}>
                    Sans GPS
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
      
      {/* Contrôles de filtre */}
      {isAdminView && (
        <div className="map-controls">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
            <h3>Filtres</h3>
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            >
              {showFilters ? <X className="h-5 w-5" /> : <Filter className="h-5 w-5" />}
            </button>
          </div>
          
          {showFilters && (
            <>
              <div className="filter-item" onClick={() => toggleFilter('chantiers')}>
                <input
                  type="checkbox"
                  className="filter-checkbox"
                  checked={filters.chantiers}
                  onChange={() => {}}
                />
                <span style={{ fontSize: 20 }}>🏗️</span>
                <span className="filter-label">Chantiers RCDS</span>
                <span className="filter-count" title={`${geoCounts.chantiers.geo} géolocalisés sur ${geoCounts.chantiers.total} total`}>
                  {geoCounts.chantiers.geo}/{geoCounts.chantiers.total}
                </span>
              </div>
              
              <div className="filter-item" onClick={() => toggleFilter('membres')}>
                <input
                  type="checkbox"
                  className="filter-checkbox"
                  checked={filters.membres}
                  onChange={() => {}}
                />
                <span style={{ fontSize: 20 }}>👥</span>
                <span className="filter-label">Membres</span>
                <span className="filter-count" title={`${geoCounts.membres.geo} géolocalisés sur ${geoCounts.membres.total} total`}>
                  {geoCounts.membres.geo}/{geoCounts.membres.total}
                </span>
              </div>
              
              <div className="filter-item" onClick={() => toggleFilter('dommages')}>
                <input
                  type="checkbox"
                  className="filter-checkbox"
                  checked={filters.dommages}
                  onChange={() => {}}
                />
                <span style={{ fontSize: 20 }}>⚠️</span>
                <span className="filter-label">Dommages</span>
                <span className="filter-count" title={`${geoCounts.dommages.geo} géolocalisés sur ${geoCounts.dommages.total} total`}>
                  {geoCounts.dommages.geo}/{geoCounts.dommages.total}
                </span>
              </div>
              
              <div className="filter-item" onClick={() => toggleFilter('travaux')}>
                <input
                  type="checkbox"
                  className="filter-checkbox"
                  checked={filters.travaux}
                  onChange={() => {}}
                />
                <span style={{ fontSize: 20 }}>🔧</span>
                <span className="filter-label">Travaux</span>
                <span className="filter-count" title={`${geoCounts.travaux.geo} géolocalisés sur ${geoCounts.travaux.total} total`}>
                  {geoCounts.travaux.geo}/{geoCounts.travaux.total}
                </span>
              </div>
              
              <div className="filter-item" onClick={() => toggleFilter('coopers')}>
                <input
                  type="checkbox"
                  className="filter-checkbox"
                  checked={filters.coopers}
                  onChange={() => {}}
                />
                <span style={{ fontSize: 20 }}>🤝</span>
                <span className="filter-label">COOPER&apos;s</span>
                <span className="filter-count" title={`${geoCounts.coopers.geo} géolocalisés sur ${geoCounts.coopers.total} total`}>
                  {geoCounts.coopers.geo}/{geoCounts.coopers.total}
                </span>
              </div>
              
              <div style={{ marginTop: 15, paddingTop: 15, borderTop: '1px solid #e5e7eb' }}>
                <div className="filter-item" onClick={() => setAutoRefresh(!autoRefresh)}>
                  <input
                    type="checkbox"
                    className="filter-checkbox"
                    checked={autoRefresh}
                    onChange={() => {}}
                  />
                  <span className="filter-label">Actualisation auto</span>
                </div>
              </div>
            </>
          )}
        </div>
      )}
      
      {/* Légende */}
      <div className="map-legend">
        <div className="legend-title">Légende</div>
        {isAdminView && (
          <>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#f59e0b' }}></div>
              <span className="legend-text">Chantiers RCDS</span>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#3b82f6' }}></div>
              <span className="legend-text">Membres</span>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#ef4444' }}></div>
              <span className="legend-text">Dommages</span>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#8b5cf6' }}></div>
              <span className="legend-text">Travaux Réparation</span>
            </div>
          </>
        )}
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: '#10b981' }}></div>
          <span className="legend-text">COOPER's Actifs</span>
        </div>
      </div>
      
      {/* V36.38 — Filtre rapide par territoire (zoom Leaflet flyTo)
          V36.45 — Repositionne en BAS-CENTRE pour eviter superposition avec stats */}
      <div
        className="territoire-quick-filter"
        data-testid="territoire-quick-filter"
        style={{
          position: 'absolute',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 999,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 6,
          padding: '8px 10px',
          background: 'rgba(10, 25, 41, 0.92)',
          border: '1px solid rgba(0, 255, 255, 0.25)',
          borderRadius: 14,
          backdropFilter: 'blur(8px)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.35)',
          maxWidth: '70vw',
        }}
      >
        {TERRITOIRE_PRESETS.map((p) => {
          const active = activeTerritoire === p.key;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => focusTerritoire(p.key)}
              data-testid={`territoire-btn-${p.key}`}
              title={`Zoom sur ${p.label}`}
              style={{
                padding: '6px 12px',
                background: active ? 'rgba(0, 255, 255, 0.85)' : 'rgba(0, 255, 255, 0.06)',
                color: active ? '#0a1929' : 'rgba(255, 255, 255, 0.92)',
                border: '1px solid ' + (active ? '#00ffff' : 'rgba(0, 255, 255, 0.25)'),
                borderRadius: 10,
                fontSize: 12,
                fontWeight: active ? 700 : 500,
                letterSpacing: 0.2,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                if (!active) e.currentTarget.style.background = 'rgba(0, 255, 255, 0.18)';
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.background = 'rgba(0, 255, 255, 0.06)';
              }}
            >
              <span style={{ marginRight: 4 }}>{p.emoji}</span>
              {p.label}
            </button>
          );
        })}
      </div>
    </>
  );
}
